import React from 'react';

import { formatPhoneNumber, formatZipCode, parsePhoneNumber, US_STATES } from '@/lib/formatters';

import styles from './UserFormFields.module.css';

export const USER_TYPES = [
  { value: 'client', label: 'Client' },
  { value: 'lawyer', label: 'Lawyer' },
  { value: 'funder', label: 'Funder' },
  { value: 'admin', label: 'Admin' },
  { value: 'growth_partner', label: 'Growth Partner' },
];

/**
 * Picks the user type above the form.
 *
 * It sits outside the field list because choosing it is what makes the Law Firm
 * and Fee Overrides sections appear — it is a mode, not one input among three.
 *
 * `locked` renders the type as a fact instead of a choice. Editing a user's role
 * from this dialog used to clear firm_id and new_firm_name without saying so;
 * the detail page at /admin/users/:id/edit remains the deliberate place to do it.
 */
export const UserTypePicker = ({ role, onChange, locked = false }) => {
  const current = USER_TYPES.find((t) => t.value === role);

  if (locked) {
    return (
      <div className={styles.typeBar}>
        <span className={styles.typeLabel}>User type</span>
        <span className={styles.typeLocked} data-testid="user-type-locked">
          {current ? current.label : role}
        </span>
        <span className={styles.typeNote} data-testid="user-type-note">
          Set at creation and cannot be changed.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.typeBar}>
      <span className={styles.typeLabel} id="user-type-label">User type</span>
      <div className={styles.typeSwitch} role="group" aria-labelledby="user-type-label">
        {USER_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            aria-pressed={role === t.value}
            onClick={() => onChange(t.value)}
            data-testid={`user-type-${t.value}`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className={styles.section}>
    <h4 className={styles.sectionHead}><span>{title}</span></h4>
    {children}
  </section>
);

/**
 * The create-user form, as the admin dialog renders it.
 *
 * Lives here rather than inline in AdminDashboard because the role-conditional
 * sections are easier to reason about — and to test — outside a 1500-line page
 * component. Existing users are edited on the detail page at
 * /admin/users/:id/edit, which builds its own fields.
 *
 * Role is not a field here. UserTypePicker sits above the form, because
 * choosing the type is what makes Law Firm and Fee Overrides appear.
 */
export const UserFormFields = ({
  formData,
  setFormData,
  formErrors = {},
  firmMode = 'select',
  setFirmMode,
  firmSearch = '',
  searchFirms,
  firmOptions = [],
  onSelectFirm,
}) => {
  const prefix = 'create';
  const set = (patch) => setFormData({ ...formData, ...patch });

  // A lawyer picks a firm, and that sets company_name server-side.
  const picksFirm = formData.role === 'lawyer';

  const error = (field) => formErrors[field] && (
    <div className={styles.error} data-testid={`${prefix}-user-${field}-error`}>
      {formErrors[field]}
    </div>
  );

  const account = (
    <Section title="Account">
      <div className={styles.rowTwo}>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Email *</label>
          <input
            className={`mcl-form-input ${formErrors.email ? 'is-invalid' : ''}`}
            type="email"
            value={formData.email}
            onChange={(e) => set({ email: e.target.value })}
            data-testid={`${prefix}-user-email`}
            placeholder="user@example.com"
          />
          {error('email')}
        </div>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Full Name *</label>
          <input
            className={`mcl-form-input ${formErrors.name ? 'is-invalid' : ''}`}
            value={formData.name}
            onChange={(e) => set({ name: e.target.value })}
            data-testid={`${prefix}-user-name`}
            placeholder="John Doe"
          />
          {error('name')}
        </div>
      </div>
    </Section>
  );

  const lawFirm = picksFirm && (
    <Section title="Law Firm">
      <div className={styles.firmSwitch} role="group" aria-label="Firm source">
        <button
          type="button"
          aria-pressed={firmMode === 'select'}
          onClick={() => setFirmMode('select')}
          data-testid="firm-mode-select"
        >
          Select Existing
        </button>
        <button
          type="button"
          aria-pressed={firmMode === 'other'}
          onClick={() => setFirmMode('other')}
          data-testid="firm-mode-other"
        >
          Add New Firm
        </button>
      </div>
      {firmMode === 'select' ? (
        <div className={styles.firmField}>
          <input
            className="mcl-form-input"
            placeholder="Search firms..."
            value={firmSearch}
            onChange={(e) => searchFirms(e.target.value)}
            data-testid="firm-search-input"
          />
          {firmOptions.length > 0 && (
            <div className={styles.firmMenu} data-testid="firm-dropdown">
              {firmOptions.map((f) => (
                <button
                  key={f.firm_id}
                  type="button"
                  className={styles.firmOption}
                  onClick={() => onSelectFirm(f)}
                  data-testid={`firm-option-${f.firm_id}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}
          {formData.firm_id && (
            <div className="mcl-form-hint">Selected: {formData.company_name}</div>
          )}
        </div>
      ) : (
        <input
          className="mcl-form-input"
          placeholder="Enter new firm name"
          value={formData.new_firm_name}
          onChange={(e) => set({ new_firm_name: e.target.value })}
          data-testid="new-firm-input"
        />
      )}
    </Section>
  );

  const phone = (
    <div className="mcl-form-group" style={{ marginBottom: 0 }}>
      <label className="mcl-form-label">Phone</label>
      <input
        className="mcl-form-input"
        value={formatPhoneNumber(formData.phone)}
        onChange={(e) => set({ phone: parsePhoneNumber(e.target.value) })}
        data-testid={`${prefix}-user-phone`}
        placeholder="(555) 123-4567"
      />
    </div>
  );

  const contact = (
    <Section title="Contact">
      {picksFirm ? phone : (
        <div className={styles.rowTwo}>
          <div className="mcl-form-group" style={{ marginBottom: 0 }}>
            <label className="mcl-form-label">Company</label>
            <input
              className="mcl-form-input"
              value={formData.company_name}
              onChange={(e) => set({ company_name: e.target.value })}
              data-testid={`${prefix}-user-company`}
              placeholder="Acme Inc."
            />
          </div>
          {phone}
        </div>
      )}
    </Section>
  );

  const address = (
    <Section title="Address">
      <div className={styles.rowAddress}>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Address 1</label>
          <input
            className="mcl-form-input"
            value={formData.address1}
            onChange={(e) => set({ address1: e.target.value })}
            data-testid={`${prefix}-user-address1`}
          />
        </div>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Apt / Suite</label>
          <input
            className="mcl-form-input"
            value={formData.address2}
            onChange={(e) => set({ address2: e.target.value })}
            data-testid={`${prefix}-user-address2`}
          />
        </div>
      </div>
      <div className={`${styles.rowCityStateZip} ${styles.stack}`}>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">City</label>
          <input
            className="mcl-form-input"
            value={formData.city}
            onChange={(e) => set({ city: e.target.value })}
            data-testid={`${prefix}-user-city`}
          />
        </div>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">State</label>
          <select
            className="mcl-form-input"
            value={formData.state}
            onChange={(e) => set({ state: e.target.value })}
            data-testid={`${prefix}-user-state`}
          >
            <option value="">Select a state</option>
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Zip</label>
          <input
            className="mcl-form-input"
            inputMode="numeric"
            value={formData.zip}
            onChange={(e) => set({ zip: formatZipCode(e.target.value) })}
            data-testid={`${prefix}-user-zip`}
            placeholder="12345"
          />
        </div>
      </div>
    </Section>
  );

  const fees = formData.role === 'funder' && (
    <Section title="Fee Overrides">
      <div className={styles.rowThree}>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Broker Fee (%)</label>
          <input
            className={`mcl-form-input ${formErrors.broker_fee_percentage ? 'is-invalid' : ''}`}
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={formData.broker_fee_percentage}
            onChange={(e) => set({ broker_fee_percentage: e.target.value })}
            data-testid={`${prefix}-broker-fee-pct`}
          />
          {error('broker_fee_percentage')
            || <div className="mcl-form-hint">0 = none. Default 15%.</div>}
        </div>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Cap ($)</label>
          <input
            className={`mcl-form-input ${formErrors.broker_fee_cap ? 'is-invalid' : ''}`}
            type="number"
            min="0"
            step="1"
            value={formData.broker_fee_cap}
            onChange={(e) => set({ broker_fee_cap: e.target.value })}
            data-testid={`${prefix}-broker-fee-cap`}
            placeholder="No cap"
          />
          {error('broker_fee_cap') || <div className="mcl-form-hint">Empty = uncapped.</div>}
        </div>
        <div className="mcl-form-group" style={{ marginBottom: 0 }}>
          <label className="mcl-form-label">Application Fee ($)</label>
          <input
            className={`mcl-form-input ${formErrors.application_fee ? 'is-invalid' : ''}`}
            type="number"
            min="0"
            step="1"
            value={formData.application_fee}
            onChange={(e) => set({ application_fee: e.target.value })}
            data-testid={`${prefix}-application-fee`}
            placeholder="Default $99"
          />
          {error('application_fee') || <div className="mcl-form-hint">0 = waived.</div>}
        </div>
      </div>
    </Section>
  );

  const revenueShare = formData.role === 'growth_partner' && (
    <div className="mcl-form-group" data-testid="gp-revenue-share-field">
      <label className="mcl-form-label" htmlFor="revenue_share_percentage">
        Revenue Share %
      </label>
      <input
        id="revenue_share_percentage"
        type="number"
        min="0"
        max="100"
        step="0.1"
        className={`mcl-form-input ${formErrors.revenue_share_percentage ? 'is-invalid' : ''}`}
        value={formData.revenue_share_percentage ?? ''}
        onChange={(e) => set({ revenue_share_percentage: e.target.value })}
        data-testid="gp-revenue-share-input"
      />
      {error('revenue_share_percentage')
        || <div className="mcl-form-hint">The partner's cut of platform revenue on their referred lawyers.</div>}
    </div>
  );

  // Balance the two columns by role: the lawyer's extra Law Firm block makes the
  // left side heavy, so Contact moves across. Everyone else keeps Contact beside
  // Account and lets Address carry the right. Stacked order stays Account, Law
  // Firm, Contact, Address, Fees either way.
  const left = picksFirm ? <>{account}{lawFirm}</> : <>{account}{contact}</>;
  const right = picksFirm ? <>{contact}{address}</> : <>{address}{fees}{revenueShare}</>;

  return (
    <div className={styles.form}>
      <div className={styles.columns}>
        <div>{left}</div>
        <div>{right}</div>
      </div>
    </div>
  );
};

export default UserFormFields;
